import { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useRadarView } from "../components/Radar.tsx";
import { HEAT_SIZE, heatColor } from "./heatmap.ts";

/** A heat grid drawn over the radar, in the radar's unzoomed overlay like the replay (zoom and pan included). */
export function HeatCanvas({ grid }: { grid: Float32Array }) {
  const { width, scale, angle, toFrame, canvasLayer: overlay, bleed } = useRadarView();
  const cw = width + bleed.left + bleed.right;
  const ch = width + bleed.top + bleed.bottom;
  const canvas = useRef<HTMLCanvasElement>(null);
  // The grid as a small picture once; the canvas stretches it to the radar.
  const picture = useMemo(() => {
    const c = document.createElement("canvas");
    c.width = HEAT_SIZE;
    c.height = HEAT_SIZE;
    const g = c.getContext("2d");
    if (!g) return c;
    const img = g.createImageData(HEAT_SIZE, HEAT_SIZE);
    for (let i = 0; i < grid.length; i++) {
      const [r, gr, b, a] = heatColor(grid[i]);
      img.data.set([r, gr, b, a], i * 4);
    }
    g.putImageData(img, 0, 0);
    return c;
  }, [grid]);

  useEffect(() => {
    const c = canvas.current;
    const g = c?.getContext("2d");
    if (!c || !g || !width) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    if (c.width !== Math.round(cw * dpr) || c.height !== Math.round(ch * dpr)) {
      c.width = Math.round(cw * dpr);
      c.height = Math.round(ch * dpr);
    }
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, cw, ch);
    g.translate(bleed.left, bleed.top);
    // The picture drawn unturned, the canvas turned with the map.
    g.translate(width / 2, width / 2);
    g.rotate((angle * Math.PI) / 180);
    g.translate(-width / 2, -width / 2);
    const a = toFrame(0, 0);
    const b = toFrame(1, 1);
    g.imageSmoothingEnabled = true;
    g.imageSmoothingQuality = "high";
    g.drawImage(picture, a.x, a.y, b.x - a.x, b.y - a.y);
  }, [picture, width, scale, angle, toFrame, cw, ch, bleed]);

  if (!overlay) return null;
  return createPortal(<canvas ref={canvas} className="replay-canvas" style={{ left: 0, top: 0, width: cw, height: ch }} aria-hidden="true" />, overlay);
}
